import { useState, useEffect } from "react";
import { useLoader } from "@/hooks/LoaderProvider";
import { useNavigate } from "react-router-dom";
import { DownOutlined, PlusOutlined } from "@/config/antd";
import PageHeader from "@/components/layout/Header/Page_Header";
import Back from "@/components/specific/Back";
import Button from "@/components/common/Button";
import SearchFilterBar from "@/components/specific/Search_&_Filter";
import CustomPagination from "@/components/specific/Pagination";
import DonationCard from "@/pages/finance_manager/components/Donation_Card";
import DonationsTable from "@/pages/finance_manager/components/Donations_Table";
import Breadcrumb from "@/components/specific/Breadcrumb";
import { useSelector } from "react-redux";
import { selectSearchTerm } from "@/store/redux/slices/search/search_slice";
import Success from "@/components/common/alerts/Success";
import Delete from "@/components/common/alerts/Delete";
import EmptyState from "@/pages/finance_manager/components/Empty_State";
// import { fetchDonationsByOrganization } from "@/store/redux/slices/donations/donations_slice";

const DonationManager = () => {
  const [searchValue, setSearchValue] = useState("");
  const [filterValue, setFilterValue] = useState("Today");
  const [filterDate, setFilterDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState("all");
  const pageSize = 10;
  const [totalDonations, setTotalDonations] = useState(100);
  const [selectedDonations, setSelectedDonations] = useState([]);
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const globalSearchTerm = useSelector(selectSearchTerm) || "";
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [deletedDonationTitle, setDeletedDonationTitle] = useState("");
  const isLoading = false;

  // Use empty data instead of dummy data
  const [donations, setDonations] = useState([]);

  // Filter donations based on activeFilter, searchValue, and globalSearchTerm
  const getFilteredDonations = () => {
    let filtered = [...donations];

    // Apply status filter
    if (activeFilter !== "all") {
      filtered = filtered.filter(
        (donation) =>
          donation.status.toLowerCase() === activeFilter.toLowerCase()
      );
    }

    // Apply search filter (local or global)
    const searchTerm = globalSearchTerm || searchValue;
    if (searchTerm) {
      filtered = filtered.filter(
        (donation) =>
          donation.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          donation.amount?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          donation.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          donation.status?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply date filter
    if (filterDate) {
      filtered = filtered.filter((donation) => {
        const donationDate = new Date(
          donation.date.split("/").reverse().join("-")
        );

        if (filterValue === "Today") {
          return (
            donationDate.getDate() === filterDate.getDate() &&
            donationDate.getMonth() === filterDate.getMonth() &&
            donationDate.getFullYear() === filterDate.getFullYear()
          );
        } else if (filterValue === "This Week") {
          // Filter for donations in the same week
          const donationTime = donationDate.getTime();
          const weekEnd = new Date(filterDate);
          weekEnd.setDate(filterDate.getDate() + 7);
          return (
            donationTime >= filterDate.getTime() &&
            donationTime < weekEnd.getTime()
          );
        } else if (filterValue === "This Month") {
          // Filter for donations in the same month
          return (
            donationDate.getMonth() === filterDate.getMonth() &&
            donationDate.getFullYear() === filterDate.getFullYear()
          );
        } else {
          // Specific date selected
          return (
            donationDate.getDate() === filterDate.getDate() &&
            donationDate.getMonth() === filterDate.getMonth() &&
            donationDate.getFullYear() === filterDate.getFullYear()
          );
        }
      });
    }

    return filtered;
  };

  const filteredDonations = getFilteredDonations();

  // Update donation count stats
  useEffect(() => {
    setTotalDonations(donations.length);
  }, [donations]);

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  // const handleRefresh = useCallback(async () => {
  //   // 1. Reset pagination to first page
  //   setCurrentPage(1);

  //   // 2. Clear the search term
  //   setSearchValue("");

  //   // 3. Reset the active filter to 'all'
  //   setActiveFilter('all');

  //   // 4. Reset the display text for the filter button
  //   setFilterValue("Today");

  //   // 5. Reset the filter date if applicable
  //   if (filterDate) {
  //     setFilterDate(new Date());
  //   }

  //   // 6. Fetch fresh donation data
  //   showLoader();
  //   try {
  //     const organizationID = getOrganizationId();

  //     // Fetch donations by organization ID if you have a similar API call
  //     // Replace with the correct action if different
  //     await dispatch(fetchDonationsByOrganization(organizationID)).unwrap();
  //   } catch (error) {
  //     console.error("Error refreshing donations:", error);
  //   } finally {
  //     hideLoader();
  //   }
  // }, [dispatch, showLoader, hideLoader]);

  const handleFilterChange = (value) => {
    setFilterValue(value);

    if (typeof value === "string" && value.match(/\d{1,2} [A-Za-z]{3} \d{4}/)) {
      // Parse the date string (format: "dd MMM yyyy")
      const parts = value.split(" ");
      const day = parseInt(parts[0], 10);
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = monthNames.indexOf(parts[1]);
      const year = parseInt(parts[2], 10);

      if (month !== -1) {
        setFilterDate(new Date(year, month, day));
      }
    } else if (value === "Today") {
      setFilterDate(new Date());
    } else if (value === "This Week") {
      // Set to start of current week
      const now = new Date();
      const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - dayOfWeek);
      setFilterDate(startOfWeek);
    } else if (value === "This Month") {
      // Set to start of current month
      const now = new Date();
      setFilterDate(new Date(now.getFullYear(), now.getMonth(), 1));
    } else {
      setFilterDate(null); // Clear date filter
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSetupClick = () => {
    // navigate('/dashboard/financial/donation/setup');
  };

  const handleDelete = (donationId, donationTitle) => {
    showLoader();
    setTimeout(() => {
      // Simulate API call
      const updatedDonations = donations.filter(
        (donation) => donation.id !== donationId
      );
      setDonations(updatedDonations);
      setDeletedDonationTitle(donationTitle);
      setIsSuccessVisible(true);
      hideLoader();
    }, 500);
  };

  const handleModify = (donationId) => {
    navigate(`/dashboard/financial/donations/edit/${donationId}`);
  };

  const handleBulkDelete = () => {
    if (selectedDonations.length === 0) return;

    showLoader();
    setTimeout(() => {
      // Get the donations being deleted
      const donationsToDelete = donations.filter((donation) =>
        selectedDonations.includes(donation.id)
      );

      // Set message based on count
      if (donationsToDelete.length === 1) {
        setDeletedDonationTitle(donationsToDelete[0].title);
      } else {
        setDeletedDonationTitle(`${donationsToDelete.length} donations`);
      }

      // Remove selected donations
      const updatedDonations = donations.filter(
        (donation) => !selectedDonations.includes(donation.id)
      );

      setDonations(updatedDonations);
      setSelectedDonations([]);
      setDeleteModalVisible(false);
      setIsSuccessVisible(true);
      hideLoader();
    }, 500);
  };

  // Function to check if we should show empty state
  const showEmptyState = () => {
    // Check if we have any donations data
    return !isLoading && (!donations || donations.length === 0);
  };

  // Function to handle "Add New" button click in EmptyState
  const handleAddNewDonation = () => {
    // Navigate to add donation page or open modal
    handleSetupClick();
  };

  // Calculate status counts
  const approvedCount = donations.filter(
    (item) => item.status?.toLowerCase() === "approved"
  ).length;

  const pendingCount = donations.filter(
    (item) => item.status?.toLowerCase() === "pending"
  ).length;

  const filterOptions = {
    items: [
      { key: "today", label: "Today" },
      { key: "thisWeek", label: "This Week" },
      { key: "thisMonth", label: "This Month" },
    ],
    onClick: handleFilterChange,
    className: "custom-dropdown-menu",
  };

  const moreOptionsMenu = {
    items: [
      { key: "1", label: "Export" },
      { key: "2", label: "Print" },
    ],
  };

  // breadcrumb items - consistent with Financial_Home
  const breadcrumbItems = [
    { title: "Financial Manager", link: "dashboard/financial" },
    { title: "Donations", link: "dashboard/financial/donations" },
  ];

  // Define sort options based on your filter buttons
  const sortOptions = [
    { key: "all", label: "All Donations" },
    { key: "approved", label: "Approved" },
    { key: "pending", label: "Pending" },
  ];

  return (
    <div className="w-full py-6 text-left sm:py-0">
      <Breadcrumb items={breadcrumbItems} />

      {/* Back Navigation */}
      <div className="mb-4">
        <Back text="Back" onClick={() => navigate("/dashboard/financial")} />
      </div>

      {/* Page Header */}
      <PageHeader title="Donations" />

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-5">
          <div className="flex flex-col text-left">
            <h1 className="heading-medium text-darkGrey">
              {totalDonations} Total Transactions
            </h1>
            <h1 className="text-regular text-darkGrey">
              Total number of donations made
            </h1>
          </div>
          <div className="items-center hidden gap-2 lg:flex">
            <Button
              text={`All Transactions (${totalDonations})`}
              color="primary"
              className="px-2 py-1 text-sm sm:px-3 sm:py-2 sm:text-small min-w-[80px] sm:min-w-[100px]"
              iconPosition="left"
              customBgColor={activeFilter === "all" ? "primary" : "white"}
              customTextColor={activeFilter === "all" ? "white" : "primary"}
              onClick={() => setActiveFilter("all")}
            />
            <Button
              text={`Approved (${approvedCount})`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "approved" ? "primary" : "white"}
              customTextColor={
                activeFilter === "approved" ? "white" : "primary"
              }
              onClick={() => setActiveFilter("approved")}
            />
            <Button
              text={`Pending (${pendingCount})`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "pending" ? "primary" : "white"}
              customTextColor={activeFilter === "pending" ? "white" : "primary"}
              onClick={() => setActiveFilter("pending")}
            />
          </div>
        </div>
        <div className="items-center hidden gap-2 lg:flex">
          <Button
            text={
              selectedDonations.length > 0
                ? "DELETE SELECTED"
                : "SETUP COLLECTION CHANNEL"
            }
            icon={<PlusOutlined />}
            color="primary"
            className="px-2 border border-primary py-1 text-small sm:px-3 sm:py-2 sm:text-small h-8 sm:h-10 min-w-[80px] sm:min-w-[100px]"
            iconPosition="left"
            customBgColor={selectedDonations.length > 0 ? "white" : "white"}
            customTextColor={selectedDonations.length > 0 ? "red" : "primary"}
            onClick={
              selectedDonations.length > 0
                ? () => setDeleteModalVisible(true)
                : handleSetupClick
            }
          />
          <Button
            text="FILTER BY"
            icon={<DownOutlined />}
            iconPosition="right"
            className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
            customBgColor="white"
            customTextColor="black"
          />
        </div>
        <div className="lg:hidden">
          <Button
            text="SETUP"
            icon={<PlusOutlined />}
            color="primary"
            className="px-2 py-1 text-small sm:px-3 sm:py-2 sm:text-small h-8 sm:h-10 min-w-[80px] sm:min-w-[100px]"
            iconPosition="left"
            customBgColor="primary"
            customTextColor="white"
            onClick={handleSetupClick}
          />
        </div>
      </div>

      <SearchFilterBar
        onSearch={handleSearch}
        // onRefresh={handleRefresh}
        onFilterChange={(value) => {
          setActiveFilter(value);
        }}
        filterValue={filterValue}
        filterOptions={filterOptions}
        moreOptionsMenu={moreOptionsMenu}
        placeholder="Search donations..."
        filterType="dropdown"
        sortOptions={sortOptions}
        searchValue={searchValue}
      />

      {isLoading ? (
        <div>Loading...</div>
      ) : showEmptyState() ? (
        <EmptyState onAddNew={handleAddNewDonation} />
      ) : (
        <>
          <div className="hidden lg:block">
            <DonationsTable
              data={filteredDonations}
              setSelectedEvents={setSelectedDonations}
              isLoading={isLoading}
              onDelete={handleDelete}
              onModify={handleModify}
            />
          </div>

          {/* Donations List for Mobile */}
          <div className="mt-6 lg:hidden">
            {filteredDonations.map((donation, index) => (
              <DonationCard
                key={index}
                id={donation.id}
                name={donation.title}
                amount={donation.amount}
                date={donation.date}
                time={donation.time}
                imgUrl={donation.imgUrl}
                status={donation.status}
                paymentMethod={donation.category}
              />
            ))}
          </div>

          {/* Show pagination only if we have data */}
          {filteredDonations.length > 0 && (
            <div className="mt-6">
              <CustomPagination
                current={currentPage}
                total={totalDonations}
                pageSize={pageSize}
                onChange={handlePageChange}
                className="pb-8"
              />
            </div>
          )}
        </>
      )}

      {/* Success Modal */}
      <Success
        isVisible={isSuccessVisible}
        onClose={() => setIsSuccessVisible(false)}
        header="Donation Deleted!"
        paragraph={`You have successfully deleted the donation from "${deletedDonationTitle}" from your list.`}
        buttonText="Close"
        onButtonClick={() => setIsSuccessVisible(false)}
      />

      {/* Delete confirmation modal */}
      <Delete
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onProceed={handleBulkDelete}
        header="Delete Selected Donations?"
        paragraph={`Are you sure you want to delete ${selectedDonations.length} selected donations? This action cannot be undone.`}
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </div>
  );
};

export default DonationManager;
